import { AccessControl } from "@components/AccessControl/AccessControl";
import { Body } from "@components/Body/Body";
import { PageHeader } from "@components/PageHeader/PageHeader";
import { PatientDeclaration } from "@components/PatientDeclaration/PatientDeclaration";
import { RestrictedPageContent } from "@components/RestrictedPageContent/RestrictedPageContent";
import useUserInfo from "@hooks/useUserInfo/useUserInfo";
import { useLocale } from "@jmc/core/src/hooks/useLocale/useLocale";
import { ImagePosition } from "@jmc/solid-design-system/src/components/molecules/Card/Card";
import { noTopPaddingFirstSection } from "@jmc/solid-design-system/src/components/molecules/HeaderHero/utils";
import { BreakPoint, useMediaQuery } from "@jmc/solid-design-system/src/hooks/useMediaQuery";
import { getLanguage } from "@jmc/solid-design-system/src/utils/languages";
import { useJnjBranding } from "@jmc/utils/hooks/useJnjBranding";
import { CMSBlock } from "@jwm/types/CMSBlock";
import { CMSHeroProps } from "@jwm/types/CMSHeroProps";
import { CMSPage } from "@jwm/types/CMSPage";
import { PageContext } from "@jwm/types/PageContext";
import { useLocation } from "@reach/router";
import { clear as clearFootnotes, set as setFootnotes } from "@redux/modules/footnotes";
import { ApplicationState } from "@redux/modules/index";
import { default as cmsMapper } from "@utils/cms-mapper";
import classNames from "classnames";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { useSiteReference } from "../contexts/SiteContext";
import { AccessLevel } from "../types/AccessLevel";
import { HeaderVisualSource } from "../types/CMSPage";
import style from "./style.module.scss";

export interface FreeFormPagePropTypes {
    data: {
        page: CMSPage;
    };
    pageContext: PageContext;
    authenticated?: boolean;
}

export const FreeFormPage = (props: FreeFormPagePropTypes): JSX.Element => {
    const siteReference = useSiteReference();
    const isDesktop = !useMediaQuery(BreakPoint.xl);
    const userInfo = useUserInfo();
    const [decorativeImage, setDecorativeImage] = useState(null);
    const dispatch = useDispatch();
    const location = useLocation();
    const locale = useLocale();
    const { jnjFullBranded } = useJnjBranding();
    const languageDirection = !locale ? 1 : getLanguage(locale?.split("-").shift())?.dir;

    useEffect(() => {
        // this allows the ContentStack Chrome extension to work
        document.body.dataset.pageref = props.data.page.uid;
        document.body.dataset.contenttype = "page";
        document.body.dataset.locale = props.data.page.locale;
    });

    useEffect(() => {
        // Clear footnotes on page change.
        dispatch(clearFootnotes());
    }, [location.pathname]);

    const {
        data: {
            page: { page_title, content, header_visual, access_control, fields, display_options },
        },
        pageContext,
    } = props;

    // Store footnotes in a redux variable
    const footnotes = useSelector((state: ApplicationState) => state.footnotes);
    if (fields?.footnotes?.length > 0 && footnotes?.length == 0) {
        dispatch(setFootnotes(fields?.footnotes));
    }

    const decorativeImageUrl = props.data.page.site_reference[0]?.branding?.decorative_image?.[0]?.visual?.url;
    const imagePosition = props?.data?.page?.site_reference[0]?.branding?.decorative_image?.[0]?.position_horizontal;

    const { page, gatedUrl, publicContent } = pageContext;
    const { access_level } = access_control;

    let imageFocus = "center-center" as ImagePosition;
    let headerVisual = null as CMSHeroProps;
    switch (header_visual.header_visual_source) {
        case HeaderVisualSource.Inherit:
            headerVisual = siteReference?.branding?.header ? siteReference?.branding?.header[0] : null;
            break;
        case HeaderVisualSource.Hero:
            headerVisual = header_visual.header_hero ? header_visual.header_hero?.[0] : null;
            headerVisual = headerVisual
                ? {
                      ...headerVisual,
                      display_options: { ...headerVisual.display_options, variant: header_visual?.variant },
                  }
                : { display_options: { variant: header_visual?.variant } };
            imageFocus = headerVisual?.display_options?.image_focus;
            break;
        case HeaderVisualSource.Carousel:
            headerVisual = header_visual?.carousel?.length
                ? {
                      ...header_visual?.carousel?.[0],
                      display_options: {
                          background_color: header_visual?.background_color,
                      },
                  }
                : null;
            break;
        case HeaderVisualSource.Image:
            headerVisual = {
                background_image: header_visual?.header_image_from_file,
                display_title: page_title,
                display_options: {
                    align_content: languageDirection ? "Left" : "Right",
                    background_color: header_visual?.background_color,
                    variant: header_visual?.variant,
                },
            };
            imageFocus = header_visual?.image_focus?.image_focus;
            break;
        case HeaderVisualSource.Colored_background:
            headerVisual = {
                display_title: page_title,
                display_options: {
                    align_content: languageDirection ? "Left" : "Right",
                    background_color: header_visual?.background_color,
                },
            };
            break;
        case HeaderVisualSource.None:
        default:
            break;
    }
    const declaration = !!siteReference?.patient_declaration?.[0]?.declaration;

    const siteSpecialty = page?.site_reference?.[0]?.related_to?.[0];

    useEffect(() => {
        if (isDesktop && decorativeImageUrl) {
            const customStyle = {
                backgroundImage: `url(${decorativeImageUrl}`,
                width: "80px",
                [imagePosition]: 0,
            };

            setDecorativeImage(<div className={classNames(style.decorativeImage)} style={customStyle} />);
        }
    }, []);

    const firstSectionColor = cmsMapper.mapSection(page?.content?.[0]?.section)?.backgroundColor;

    const removePaddingFirstSection =
        jnjFullBranded &&
        noTopPaddingFirstSection(
            firstSectionColor,
            headerVisual?.display_options?.background_color,
            headerVisual?.display_options?.variant,
            headerVisual?.background_image?.gatsbyImageData,
            headerVisual?.hero_video?.length > 0,
            !!headerVisual,
        );

    return (
        <div className={classNames(style.wrapper)}>
            {decorativeImage}
            <PatientDeclaration siteReference={siteReference} access_level={access_level}>
                {access_level === AccessLevel.Private && !userInfo.isAuthenticated() && !declaration ? (
                    <RestrictedPageContent />
                ) : (
                    <PageHeader
                        title={page_title}
                        headerVisual={headerVisual}
                        headerSize={header_visual.header_image_size}
                        placement={`page:${page_title.toLowerCase()}`}
                        siteSpecialty={siteSpecialty}
                        imageFocus={imageFocus?.toLowerCase()}
                        firstSectionColor={firstSectionColor}
                    />
                )}
                <AccessControl
                    gatedUrl={gatedUrl}
                    publicContent={publicContent}
                    content={declaration ? page?.content : content}
                    level={access_level === null ? AccessLevel.Public : access_level}
                    declaration={declaration}
                >
                    {(pageData: { content: CMSBlock[] }): JSX.Element => {
                        return (
                            <Body
                                body={pageData?.content}
                                placement={`page:${page_title.toLowerCase()}`}
                                show_toc={display_options?.show_toc}
                                background_color={display_options?.background_color}
                                hasHeader={headerVisual ? true : false}
                                headerImageSize={header_visual.header_image_size}
                                siteSpecialty={siteSpecialty}
                                noTopPaddingFirstSection={removePaddingFirstSection}
                            />
                        );
                    }}
                </AccessControl>
            </PatientDeclaration>
        </div>
    );
};

export default FreeFormPage;
